import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Activity, Plus, Flame, Clock, TrendingUp } from 'lucide-react';
import { mockExerciseLogs } from '../mock';
import { toast } from '../hooks/use-toast';
import { Textarea } from '../components/ui/textarea';

const Exercise = () => {
  const [exercises, setExercises] = useState(mockExerciseLogs);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newExercise, setNewExercise] = useState({
    exercise_type: 'Walking',
    duration: '',
    intensity: 'Moderate',
    calories_burned: '',
    notes: '',
    time: new Date().toISOString().slice(0, 16)
  });

  const handleAddExercise = () => {
    if (!newExercise.duration || !newExercise.calories_burned) {
      toast({
        title: "Error",
        description: "Please enter duration and calories burned",
        variant: "destructive"
      });
      return;
    }

    const exercise = {
      log_id: `ex_${Date.now()}`,
      user_id: 'user_001',
      exercise_type: newExercise.exercise_type,
      duration: parseInt(newExercise.duration),
      intensity: newExercise.intensity,
      calories_burned: parseInt(newExercise.calories_burned),
      time: new Date(newExercise.time),
      notes: newExercise.notes
    };

    setExercises([exercise, ...exercises]);
    setDialogOpen(false);
    setNewExercise({
      exercise_type: 'Walking',
      duration: '',
      intensity: 'Moderate',
      calories_burned: '',
      notes: '',
      time: new Date().toISOString().slice(0, 16)
    });

    toast({
      title: "Success!",
      description: "Exercise activity logged successfully"
    });
  };

  const totalDuration = exercises.reduce((sum, ex) => sum + ex.duration, 0);
  const totalCalories = exercises.reduce((sum, ex) => sum + ex.calories_burned, 0);
  const weeklyGoal = 150; // minutes
  const goalProgress = Math.round((totalDuration / weeklyGoal) * 100);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Exercise Tracker</h2>
          <p className="text-gray-600 mt-1">Track your physical activities and workouts</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 shadow-lg hover:shadow-xl transition-all">
              <Plus className="w-4 h-4 mr-2" />
              Log Activity
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Log Exercise Activity</DialogTitle>
              <DialogDescription>
                Record your workout session
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label htmlFor="exercise-type">Exercise Type</Label>
                <Select
                  value={newExercise.exercise_type}
                  onValueChange={(value) => setNewExercise({ ...newExercise, exercise_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Walking">Walking</SelectItem>
                    <SelectItem value="Running">Running</SelectItem>
                    <SelectItem value="Cycling">Cycling</SelectItem>
                    <SelectItem value="Swimming">Swimming</SelectItem>
                    <SelectItem value="Yoga">Yoga</SelectItem>
                    <SelectItem value="Weight Training">Weight Training</SelectItem>
                    <SelectItem value="Dancing">Dancing</SelectItem>
                    <SelectItem value="Sports">Sports</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="duration">Duration (minutes)</Label>
                  <Input
                    id="duration"
                    type="number"
                    placeholder="30"
                    value={newExercise.duration}
                    onChange={(e) => setNewExercise({ ...newExercise, duration: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="calories">Calories Burned</Label>
                  <Input
                    id="calories"
                    type="number"
                    placeholder="200"
                    value={newExercise.calories_burned}
                    onChange={(e) => setNewExercise({ ...newExercise, calories_burned: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="intensity">Intensity</Label>
                <Select
                  value={newExercise.intensity}
                  onValueChange={(value) => setNewExercise({ ...newExercise, intensity: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Light">Light</SelectItem>
                    <SelectItem value="Moderate">Moderate</SelectItem>
                    <SelectItem value="Vigorous">Vigorous</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="time">Date & Time</Label>
                <Input
                  id="time"
                  type="datetime-local"
                  value={newExercise.time}
                  onChange={(e) => setNewExercise({ ...newExercise, time: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes (optional)</Label>
                <Textarea
                  id="notes"
                  placeholder="How did you feel?"
                  value={newExercise.notes}
                  onChange={(e) => setNewExercise({ ...newExercise, notes: e.target.value })}
                />
              </div>
              <Button onClick={handleAddExercise} className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600">
                Log Activity
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats Cards */}
      <div className="grid md:grid-cols-3 gap-6">
        <Card className="shadow-md border-0">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Time</CardTitle>
            <Clock className="w-5 h-5 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">{totalDuration} min</div>
            <p className="text-sm text-gray-600 mt-1">This week</p>
          </CardContent>
        </Card>

        <Card className="shadow-md border-0">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Calories Burned</CardTitle>
            <Flame className="w-5 h-5 text-orange-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">{totalCalories}</div>
            <p className="text-sm text-gray-600 mt-1">Total this week</p>
          </CardContent>
        </Card>

        <Card className="shadow-md border-0">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Weekly Goal</CardTitle>
            <TrendingUp className="w-5 h-5 text-emerald-600" />
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">{goalProgress}%</div>
            <p className="text-sm text-gray-600 mt-1">{totalDuration}/{weeklyGoal} mins</p>
          </CardContent>
        </Card>
      </div>

      {/* Progress Bar */}
      <Card className="shadow-md border-0">
        <CardHeader>
          <CardTitle>Weekly Activity Goal</CardTitle>
          <CardDescription>Aim for 150 minutes of moderate activity per week</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm font-medium">
              <span className="text-gray-700">Progress</span>
              <span className="text-emerald-600">{totalDuration} / {weeklyGoal} minutes</span>
            </div>
            <div className="relative w-full h-6 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="absolute left-0 top-0 h-full bg-gradient-to-r from-orange-400 to-red-500 rounded-full transition-all duration-500"
                style={{ width: `${Math.min(goalProgress, 100)}%` }}
              />
              <div className="absolute inset-0 flex items-center justify-center text-xs font-semibold text-white">
                {goalProgress}%
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Exercise List */}
      <Card className="shadow-md border-0">
        <CardHeader>
          <CardTitle>Activity History</CardTitle>
          <CardDescription>Your recent exercise sessions</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {exercises.map((exercise, index) => {
              const intensityColors = {
                Light: 'bg-green-100 text-green-700 border-green-200',
                Moderate: 'bg-blue-100 text-blue-700 border-blue-200',
                Vigorous: 'bg-orange-100 text-orange-700 border-orange-200'
              };

              return (
                <div
                  key={index}
                  className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-gradient-to-r from-orange-50 to-white rounded-lg border border-gray-200 hover:shadow-md transition-all gap-3"
                >
                  <div className="flex items-center space-x-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-orange-400 to-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Activity className="w-7 h-7 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{exercise.exercise_type}</p>
                      <p className="text-sm text-gray-600">
                        {new Date(exercise.time).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                        {' at '}
                        {new Date(exercise.time).toLocaleTimeString('en-US', {
                          hour: 'numeric',
                          minute: '2-digit'
                        })}
                      </p>
                      {exercise.notes && (
                        <p className="text-xs text-gray-500 mt-1 italic">{exercise.notes}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-4 sm:gap-6 text-sm">
                    <div className="text-center">
                      <p className="font-bold text-blue-700">{exercise.duration} min</p>
                      <p className="text-xs text-gray-600">Duration</p>
                    </div>
                    <div className="text-center">
                      <p className="font-bold text-orange-700">{exercise.calories_burned}</p>
                      <p className="text-xs text-gray-600">Calories</p>
                    </div>
                    <div className="text-center">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium border ${intensityColors[exercise.intensity]}`}>
                        {exercise.intensity}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Exercise;
